import { LOCALE_DIRECTION, type Locale } from '@aysar/contracts';
import { useEffect, useMemo } from 'react';

import { i18n } from '@/shared/i18n/i18n.config';
import { appSettingsApi } from '@/shared/services/app-settings/app-settings.api';
import { selectLocale } from '@/shared/store/reducers/preferences/preferences.slice';
import { useAppDispatch, useAppSelector } from '@/shared/store/typed-hooks/typed-hooks';

export type Direction = 'rtl' | 'ltr';

export interface DirectionState {
  locale: Locale;
  direction: Direction;
}

/**
 * Makes the locale in the store the one source of language and direction.
 *
 * Flow is strictly one-way — slice → i18next, slice → `<html>` — and nothing
 * writes back. Two-way sync between redux and an i18next singleton is a loop
 * waiting for the first race; here the slice is authoritative, `LocaleToggle`
 * dispatches to it, and everything downstream follows.
 *
 * Direction is derived from the locale (U7) and never stored separately: a
 * `direction` field could drift out of step with the language, and no component
 * branches on it anyway — they use logical properties and let `dir` mirror them.
 */
export function useDirectionProviderLogic(): DirectionState {
  const dispatch = useAppDispatch();
  const locale = useAppSelector(selectLocale);

  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dir = LOCALE_DIRECTION[locale];
  }, [locale]);

  useEffect(() => {
    if (i18n.language !== locale) {
      void i18n.changeLanguage(locale);
    }
  }, [locale]);

  // `brand.name` is resolved per `Accept-Language`, so the cached payload is the
  // OLD language's the moment the locale changes (P28).
  useEffect(() => {
    dispatch(appSettingsApi.util.invalidateTags(['AppSettings']));
  }, [dispatch, locale]);

  return useMemo(() => ({ locale, direction: LOCALE_DIRECTION[locale] }), [locale]);
}
